import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { mapRetrievalToRedux } from '../Integrations/mapper';
import { calculateAge } from '../utils/utilFunctions';
import {
  createApplication,
  getInterestRate,
  getMemberDetails,
  updateAccountDetails,
  retrieveApplication,
} from './asyncActions';
import { aboutYouInitialState, applicantInitialState } from './initialState';
import { RootState } from './store';
import {
  AboutYouState,
  AccountDetailsState,
  ApplicantsState,
  FinancialDetailsState,
  MemberDetailsState,
  TaxDetailsState,
  VerifyIdentityState,
} from './types';
import moment from 'moment';

const initialState: ApplicantsState = {
  interestRateFetchingStatus: '',
  currentApplicantIndex: 0,
  applicants: [applicantInitialState],
};

/* Reducers */
export const applicantsSlice = createSlice({
  name: 'applicants',
  initialState,
  reducers: {
    initApplicant: (state, action: PayloadAction<number>) => {
      state.applicants[action.payload] = applicantInitialState;
    },
    addApplicant: (state) => {
      state.applicants.push(applicantInitialState);
    },
    removeApplicant: (state) => {
      state.applicants.pop();
    },
    setCurrentApplicantIndex: (state, action: PayloadAction<number>) => {
      state.currentApplicantIndex = action.payload;
    },
    setApplicantsId: (state, action: PayloadAction<string[]>) => {
      const applicantsIdArray = action.payload;
      applicantsIdArray.forEach((id, index) => {
        state.applicants[index].applicantId = id;
      });
    },
    setApplicantsIdForAdditionalCardholders: (
      state,
      action: PayloadAction<string[]>
    ) => {
      const additionalCardholdersIdArray = action.payload;
      additionalCardholdersIdArray.forEach((id, index) => {
        state.applicants[index + 1].applicantId = id;
      });
    },
    setMemberDetails: (
      state,
      action: PayloadAction<{
        applicantIndex: number;
        memberDetails: MemberDetailsState;
      }>
    ) => {
      state.applicants[action.payload.applicantIndex].memberDetails = {
        ...action.payload.memberDetails,
        password: '',
      };
    },
    setAboutYou: (
      state,
      action: PayloadAction<{
        aboutYou: AboutYouState;
        applicantIndex: number;
      }>
    ) => {
      state.applicants[action.payload.applicantIndex].aboutYou =
        action.payload.aboutYou;
    },
    setVerifyIdentity: (state, action: PayloadAction<VerifyIdentityState>) => {
      state.applicants[state.currentApplicantIndex].verifyIdentity =
        action.payload;
    },
    setVerifyIdentityNames: (
      state,
      action: PayloadAction<{
        firstName: string;
        middleNames: string;
        lastName: string;
      }>
    ) => {
      state.applicants[state.currentApplicantIndex].aboutYou.firstName =
        action.payload.firstName;

      state.applicants[state.currentApplicantIndex].aboutYou.middleName =
        action.payload.middleNames;

      state.applicants[state.currentApplicantIndex].aboutYou.lastName =
        action.payload.lastName;
    },
    setAccountDetails: (
      state,
      action: PayloadAction<{
        accountDetails: AccountDetailsState;
        applicantIndex: number;
      }>
    ) => {
      state.applicants[action.payload.applicantIndex].accountDetails =
        action.payload.accountDetails;
    },
    setFinancialDetails: (
      state,
      action: PayloadAction<FinancialDetailsState>
    ) => {
      state.applicants[state.currentApplicantIndex].financialDetails =
        action.payload;
    },
    setTaxDetails: (state, action: PayloadAction<TaxDetailsState>) => {
      state.applicants[state.currentApplicantIndex].taxDetails = action.payload;
    },
    setDefaultAdditionalAccountToOpen: (
      state,
      action: PayloadAction<string>
    ) => {
      state.applicants[
        state.currentApplicantIndex
      ].accountDetails.additionalAccountToOpen = action.payload;
    },
    setInterestRate: (
      state,
      action: PayloadAction<{
        rate: number;
        interestRateFetchingStatus: string;
      }>
    ) => {
      // Only primary applicant has Account details step to set interest rate for Term deposit account
      state.applicants[0].accountDetails.interestRate = (action.payload.rate * 100).toFixed(2);
      state.interestRateFetchingStatus = action.payload.interestRateFetchingStatus;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createApplication.fulfilled, (state, action) => {
        applicantsSlice.caseReducers.setApplicantsId(state, {
          payload: action.payload.applicantIds,
          type: 'applicants.setApplicantsId',
        });
      })
      .addCase(getMemberDetails.fulfilled, (state, action: any) => {
        const { applicantIndex, ...res } = action.payload;
        const memberDetails = res;

        applicantsSlice.caseReducers.setAboutYou(state, {
          payload: {
            aboutYou: {
              ...aboutYouInitialState,
              firstName: memberDetails.firstName,
              middleName: memberDetails.middleName,
              lastName: memberDetails.surname,
              emailAddress: memberDetails.emailAddress,
              mobileNumber: memberDetails.mobilePhone,
              birthday: moment(memberDetails.dob).format('DDMMYYYY'),
              age: calculateAge(memberDetails.dob),
            },
            applicantIndex: applicantIndex,
          },
          type: 'applicants/setAboutYou',
        });
      })
      .addCase(updateAccountDetails.fulfilled, (state, action) => {
        // Only set additional cardholder applicant ID when not on save for later,
        // cause we only update applicants on submit (Save and continue).
        if (
          action.payload.additionalCardholderIds &&
          !action.meta.arg.saveForLater
        ) {
          applicantsSlice.caseReducers.setApplicantsIdForAdditionalCardholders(
            state,
            {
              payload: action.payload.additionalCardholderIds,
              type: 'applicants.setApplicantsIdForAdditionalCardholders',
            }
          );
        }
      })
      .addCase(getInterestRate.pending, (state) => {
        state.interestRateFetchingStatus = 'fetching';
      })
      .addCase(getInterestRate.fulfilled, (state, action: any) => {
        applicantsSlice.caseReducers.setInterestRate(state, {
          payload: {
            rate: action.payload.rate, // + Math.floor(Math.random() * 10 + 1),
            interestRateFetchingStatus: 'success',
          },
          type: 'applicants.setInterestRate',
        });

        if (action.payload.rate === 0) {
          applicantsSlice.caseReducers.setInterestRate(state, {
            payload: {
              rate: action.payload.rate,
              interestRateFetchingStatus: 'fail'
            },
            type: 'applicants.setInterestRate',
          });
        }
      })
      .addCase(getInterestRate.rejected, (state) => {
        applicantsSlice.caseReducers.setInterestRate(state, {
          payload: {
            rate: 0,
            interestRateFetchingStatus: 'fail',
          },
          type: 'applicants.setInterestRate',
        });
      })
      .addCase(retrieveApplication.fulfilled, (state, action: any) => {
        const retrievalData = action.payload;
        const retrievalApplicants = retrievalData.retrieveApplicants;

        state.currentApplicantIndex = retrievalData.currentApplicantIndex;
        state.applicants = mapRetrievalToRedux(retrievalApplicants);
      });
  },
});

export const {
  initApplicant,
  addApplicant,
  removeApplicant,
  setCurrentApplicantIndex,
  setApplicantsId,
  setMemberDetails,
  setAboutYou,
  setVerifyIdentity,
  setVerifyIdentityNames,
  setAccountDetails,
  setTaxDetails,
  setFinancialDetails,
  setDefaultAdditionalAccountToOpen,
  setInterestRate,
} = applicantsSlice.actions;

export const selectApplicants = (state: RootState): ApplicantsState =>
  state.applicants;

export default applicantsSlice.reducer;
